// Common utils
import React from 'react';
import { Badge } from 'reactstrap';

// TODO: save to storage / submit to api
const logLines = [];
function log(...args) {
  const dateString = `[${new Date()}]`;
  logLines.push([dateString, ...args].join(' '));

  // TODO: add prod mode that doesn't print?
  console.log(dateString, ...args);
}

function storageSet(items) {
  return new Promise((resolve) => {
    // Only use chrome storage in extension mode. Chrome storage can store objects so no need to JSON stringify
    if (window.chrome.storage) {
      window.chrome.storage.local.set(items, () => resolve());
    } else {
      // Use browser storage instead
      Object.entries(items).forEach(([key, val]) =>
        localStorage.setItem(key, JSON.stringify(val))
      );
      resolve();
    }
  });
}

function storageGet(keys) {
  return new Promise((resolve) => {
    // Only use chrome storage in extension mode
    if (window.chrome.storage) {
      window.chrome.storage.local.get(keys, (items) => {
        resolve(items);
      });
    } else {
      // Use browser storage instead
      let results = {};
      for (const [key, val] of Object.entries(localStorage)) {
        // Browser localStorage only stores strings, so parse them before returning
        results[key] = JSON.parse(val);
      }
      resolve(results);
    }
  });
}

function storageClear() {
  return new Promise((resolve) => {
    window.chrome.storage.local.clear(resolve);
  });
}

function isDevMode() {
  return process.env.NODE_ENV === 'development';
}

function getEndpoint() {
  if (isDevMode()) {
    return 'http://localhost:4200';
  } else {
    return 'https://api.snailbot.io';
  }
}

function titleCase(string) {
  return string[0].toUpperCase() + string.slice(1).toLowerCase();
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function getUser() {
  let user = null;
  let license = null;

  let errorMessage = null;

  try {
    const response = await fetch(`${await getEndpoint()}/auth/user`, {
      credentials: 'include',
    });

    const data = await response.json();
    ({ user, license } = data);
  } catch (err) {
    errorMessage = 'There was an error contacting the server.';
  }

  return { user, license, errorMessage };
}

async function getProducts(vendorAccountId) {
  let products = null;
  let errorMessage = null;

  try {
    const response = await fetch(
      `${await getEndpoint()}/products/${vendorAccountId}`,
      {
        credentials: 'include',
      }
    );

    const data = await response.json();
    products = data.products;
  } catch (err) {
    errorMessage = 'There was an error contacting the server.';
  }

  return { products, errorMessage };
}

async function getTrackedProducts(vendorAccountId) {
  let intentMap = null;
  let errorMessage = null;

  try {
    const response = await fetch(
      `${await getEndpoint()}/products/tracked/${vendorAccountId}`,
      {
        credentials: 'include',
      }
    );

    const data = await response.json();
    intentMap = data.intentMap;
  } catch (err) {
    errorMessage = 'There was an error contacting the server.';
  }

  return { intentMap, errorMessage };
}

async function putTrackedProducts(vendorAccountId, intentMap, setToIntentMap) {
  let success = false;
  let errorMessage = null;

  try {
    const response = await fetch(`${await getEndpoint()}/products/tracked`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        vendorAccountId,
        intentMap,
        setToIntentMap,
      }),
    });

    if (response.ok) {
      success = true;
    } else {
      errorMessage =
        'There was an error updating products. Please refresh and try again.';
    }
  } catch (err) {
    errorMessage =
      'There was an error updating products. Please refresh and try again.';
  }

  return { success, errorMessage };
}

async function getTrends() {
  let trends = null;
  let errorMessage = null;

  try {
    const response = await fetch(`${await getEndpoint()}/trends`, {
      credentials: 'include',
    });

    const data = await response.json();
    trends = data.trends;
  } catch (err) {
    errorMessage = 'There was an error contacting the server.';
  }

  return { trends, errorMessage };
}

/**
 * Try to parse carrier from item.
 * `item.carrierCode` and `item.carrierId` are from Amazon.
 * Carrier code is usually present and carrier ID has never been observed to be different from it.
 * (eg: carrierCode? === "UPS" === carrierId?)
 * @param {VendorOrderItem} item
 * @returns {string | undefined}
 */
function parseCarrier(item) {
  return item.carrierCode || item.carrierId || undefined;
}

/**
 * @param {VendorOrderItem[]} items
 * @param {OrderHistoryProduct[]} products
 */
function patchOrderItems(items, products) {
  for (const item of items) {
    // Normalize shipping carrier.
    item.carrier = parseCarrier(item);

    // Add reference to matching product object.
    item.product = products.find(
      (product) => product.vendorProductId === item.vendorProductId
    );
  }
}

/**
 * Makes a request for the logged in user's order history for a particular instance.
 * @param {InstanceSlot} credential - The instance credential.
 * @param {OrderHistory.CachePreference} cachePreference - The cache preference.
 * @param {AbortSignal} signal - A signal to associate with the request.
 * @returns {Promise<OrdersOutput>}
 */
async function getOrders(credential, cachePreference = 'db', signal) {
  let response = null;
  let errorMessage = null;

  try {
    let query = {
      cache: cachePreference,
    };

    switch (cachePreference) {
      case 'db':
      case 'liveormemory':
        query['vendorAccountId'] = credential.account.vendorAccountId;
        break;
      default:
        throw new Error('Invalid cache preference.');
    }

    const params = new URLSearchParams(query);

    const rawResponse = await fetch(`${await getEndpoint()}/orders?${params}`, {
      credentials: 'include',
      signal,
    });

    response = await rawResponse.json();

    if (response.orders && response.products) {
      patchOrderItems(
        response.orders.map((x) => x.items).flat(),
        response.products
      );
    }
  } catch (err) {
    errorMessage = 'There was an error contacting the server.';
  }

  return { response, errorMessage };
}

async function putCredentials(accountToPut) {
  let account = null;
  let errorMessage = null;

  try {
    const response = await fetch(`${await getEndpoint()}/credentials`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(accountToPut),
    });
    account = await response.json();
  } catch (err) {
    errorMessage =
      "There was an error setting credentials. Make sure you filled out all fields and aren't adding an account already on another instance. If it still errors, try Incognito or a different browser.";
  }

  return { account, errorMessage };
}

async function submitTestOrder({ vendor, url, vendorAccountId, storeId }) {
  let errorMessage =
    'There was an issue submitting your test order request. Please try another product.';

  try {
    const response = await fetch(`${await getEndpoint()}/events/testorder`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        vendor,
        url,
        vendorAccountId,
        storeId,
      }),
    });
    if (response.ok) errorMessage = null;
  } catch (err) {
    // no-op
  }

  return { errorMessage };
}

async function putWebhook(webhookUrl) {
  let success = false;
  let errorMessage = null;

  try {
    const response = await fetch(`${await getEndpoint()}/credentials/webhook`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        webhookUrl,
      }),
    });

    if (response.ok) {
      success = true;
    } else {
      errorMessage =
        'There was an error setting your webhook. Please make sure it is the right format.';
    }
  } catch (err) {
    errorMessage =
      'There was an error setting your webhook. Please make sure it is the right format.';
  }

  return { success, errorMessage };
}

async function getCredentials() {
  let credentials = null;
  let errorMessage = null;

  try {
    const response = await fetch(`${await getEndpoint()}/credentials`, {
      credentials: 'include',
    });
    credentials = await response.json();
  } catch (err) {
    errorMessage =
      'There was an error getting credentials. Please refresh and try again.';
  }

  return { credentials, errorMessage };
}

async function getStripeCustomerInfo() {
  let result = null;
  let errorMessage = null;

  try {
    const response = await fetch(`${await getEndpoint()}/stripe/customer`, {
      credentials: 'include',
    });
    result = await response.json();
  } catch (err) {
    errorMessage =
      'Error getting Stripe customer info. Please try refreshing or contacting support.';
  }

  return { errorMessage, ...result };
}

const EVENT_TYPES = {
  AM_LOGIN: 'am_login',
  AM_CHECKOUT_ATTEMPT: 'am_checkout_attempt',
  AM_CHECKOUT_ATTEMPT_SKIPPED: 'am_checkout_attempt_skipped',
  WM_CHECKOUT_ATTEMPT: 'wm_checkout_attempt',
  WM_CHECKOUT_ATTEMPT_SKIPPED: 'wm_checkout_attempt_skipped',
  TG_CHECKOUT_ATTEMPT: 'tg_checkout_attempt',
  TG_CHECKOUT_ATTEMPT_SKIPPED: 'tg_checkout_attempt_skipped',
};

const vendorToAttemptEvent = {
  amazon: EVENT_TYPES.AM_CHECKOUT_ATTEMPT,
  walmart: EVENT_TYPES.WM_CHECKOUT_ATTEMPT,
  target: EVENT_TYPES.TG_CHECKOUT_ATTEMPT,
};

const vendorToAttemptSkippedEvent = {
  amazon: EVENT_TYPES.AM_CHECKOUT_ATTEMPT_SKIPPED,
  walmart: EVENT_TYPES.WM_CHECKOUT_ATTEMPT_SKIPPED,
  target: EVENT_TYPES.TG_CHECKOUT_ATTEMPT_SKIPPED,
};

const AM_SKIP_REASONS = {
  NO_INTENT: 'no_intent',
  EXCEEDS_MAX_PRICE: 'exceeds_max_price',
  LIMIT_REACHED: 'limit_reached',
};

function getDpUrlForVendor(vendor, vendorProductId) {
  if (vendor === 'amazon') {
    return `https://www.amazon.com/dp/${vendorProductId}`;
  } else if (vendor === 'walmart') {
    return `https://www.walmart.com/ip/${vendorProductId}`;
  } else if (vendor === 'target') {
    return `https://www.target.com/p/A-${vendorProductId}`;
  } else {
    return '';
  }
}

async function getEvents(vendor, vendorAccountId, eventTypes, lookbackMs) {
  let events = null;
  let errorMessage = null;

  try {
    const response = await fetch(`${await getEndpoint()}/events`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        vendor,
        vendorAccountId,
        eventTypes,
        lookbackMs,
      }),
    });
    if (response.ok) {
      const data = await response.json();
      events = data.events;
    } else {
      errorMessage = 'Server did not return events! Reach out for help.';
    }
  } catch (err) {
    errorMessage =
      'There was an error contacting the server! Reach out for help.';
  }

  return { events, errorMessage };
}

async function getCheckouts() {
  let checkouts = null;
  let errorMessage = null;

  try {
    const response = await fetch(`${await getEndpoint()}/events/checkouts`, {
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      checkouts = data.checkouts;
    } else {
      errorMessage = 'Server did not return checkouts! Reach out for help.';
    }
  } catch (err) {
    errorMessage =
      'There was an error contacting the server! Reach out for help.';
  }

  return { checkouts, errorMessage };
}

async function getIPData() {
  const output = {
    location: null,
    ip: null,
  };
  try {
    const response = await fetch(
      `https://pro.ip-api.com/json?key=4RiDVW5CCzDq2Jj`
    );

    if (response.ok) {
      const data = await response.json();
      if (data.status === 'success') {
        if (data.city && data.region) {
          output.location = `${data.city}, ${data.region}`;
        }
        output.ip = data.query;
      }
    }
  } catch (err) {}

  return output;
}

function hasTouch() {
  return window.matchMedia('(any-pointer: coarse)').matches;
}

function hasMouse() {
  return matchMedia('(pointer:fine)').matches;
}

function isTouchDevice() {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  );
}

function isPC() {
  return hasMouse() || !isTouchDevice();
}

function getUserAgent() {
  return navigator.userAgent;
}

function getCookies() {
  const cookies = {};
  for (const kv of document.cookie.split('; ')) {
    const [key, val] = kv.split('=');
    cookies[key] = val;
  }
  return cookies;
}

function getAsinFromDPUrl(url) {
  const pattern = /(?:[\/dp\/]|$)([A-Z0-9]{10})/;
  const result = pattern.exec(url);
  if (!result) return null;
  return result[1] || null;
}

function getTcinFromDPUrl(url) {
  const pattern = /\/A-(\d+)/;
  const result = pattern.exec(url);
  if (!result) return null;
  return result[1] || null;
}

function setCookie(key, val) {
  // default max age 1 yr
  document.cookie = `${key}=${val} ;path=/; max-age=31536000`;
}

const SELECTED_CRED_ID_COOKIE_NAME = 'selectedCredId';
function saveCredIdToCookie(credId) {
  setCookie(SELECTED_CRED_ID_COOKIE_NAME, credId);
}
function getCredIdFromCookie() {
  return getCookies()[SELECTED_CRED_ID_COOKIE_NAME];
}

function renderCredBadge(cred) {
  let color = 'success';
  if (cred.vendor === 'amazon') {
    color = 'success';
  } else if (cred.vendor === 'walmart') {
    color = 'primary';
  } else if (cred.vendor === 'target') {
    color = 'danger';
  }

  let text = cred.vendor;
  text += ` ${cred.vaType || 'Pro'}`;
  text += ` ${cred.slotNum}`;

  return (
    <Badge color={color} className="mr-2">
      {text}
    </Badge>
  );
}

function isProUpgradeExpired(notifyTime) {
  if (!notifyTime) return true;
  return Date.now() - new Date(notifyTime).getTime() > 1000 * 60 * 60 * 24 * 3;
}

const maybePluralize = (count, noun, suffix = 's') =>
  `${noun}${count !== 1 ? suffix : ''}`;

function makeDereferUrl(url) {
  return `https://dereferer.me/?${encodeURIComponent(url)}`;
}

function getPrettyTime(value) {
  return new Date(value).toLocaleString(undefined, {
    timeStyle: 'short',
    dateStyle: 'short',
  });
}

function getPrettyDate(value) {
  return new Date(value).toLocaleString(undefined, {
    timeZone: 'UTC',
    dateStyle: 'short',
  });
}

function getPrettyDateOrTime(value) {
  const valueLength = value?.length ?? 0;
  return valueLength === 10 ? getPrettyDate(value) : getPrettyTime(value);
}

/**
 * Creates a `Date` object with `value` and returns it if it's valid. It returns `null` otherwise.
 * https://stackoverflow.com/questions/1353684/detecting-an-invalid-date-date-instance-in-javascript
 * @param {?string} value
 * @returns {?Date}
 */
function constructDate(value) {
  if (!value) return null;

  const date = new Date(value);
  if (isNaN(date.getTime())) return null;

  return date;
}

/**
 * https://stackoverflow.com/a/66390028
 * @type {Array.<{ unit: Intl.RelativeTimeFormatUnit, ms: number }> }
 */
const units = [
  { unit: 'year', ms: 31536000000 },
  { unit: 'month', ms: 2628000000 },
  { unit: 'day', ms: 86400000 },
  { unit: 'hour', ms: 3600000 },
  { unit: 'minute', ms: 60000 },
  { unit: 'second', ms: 1000 },
];
const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/**
 * Get language-sensitive relative time message from Dates.
 * @param {?Date} relative  - the relative dateTime, generally is in the past or future
 * @param {Date=} pivot     - the dateTime of reference, generally is the current time
 * @returns {?string}
 */
function relativeTimeFromDates(relative, pivot = new Date()) {
  if (!relative) return;
  const elapsed = relative.getTime() - pivot.getTime();
  return relativeTimeFromElapsed(elapsed);
}

/**
 * Get language-sensitive relative time message from elapsed time.
 * @param {number} elapsed   - the elapsed time in milliseconds
 * @returns {?string}
 */
function relativeTimeFromElapsed(elapsed) {
  for (const { unit, ms } of units) {
    if (Math.abs(elapsed) >= ms || unit === 'second') {
      const formatted = rtf.format(Math.round(elapsed / ms), unit);
      return formatted === 'now' ? 'just now' : formatted;
    }
  }
}

/**
 * Inject snailmetrix (which is designed to run on load) into the current page.
 * @param {{ source: string, vendorAccountId: string }} - The load source (`credentials` or `testorder`) and the vendor account id.
 */
function injectSnailMetrix({ source, vendorAccountId }) {
  const params = new URLSearchParams({
    source,
    vendorAccountId,
  });
  const url = `${process.env.PUBLIC_URL}/scripts/snailmetrix.js?${params}`;
  const script = document.createElement('script');
  script.src = url;
  script.async = true;
  document.body.appendChild(script);
}

function getUrlParamVal(name) {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

/**
 * @typedef {Object} LocalStorage
 * @property {string|null} mainDomainVal Value on target.com
 * @property {string|null} img9Val Value on img9.target.com
 */

/**
 * Gets local storage lsa/lsb values for a user's serialized fingerprint for Target.
 * @param {string} vendorAccountId
 * @returns {Promise<LocalStorage>}
 */
async function getSnailMetrixLocalStorage(vendorAccountId) {
  const response = await fetch(`${getEndpoint()}/snailmetrix/fetch-storage`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      vendorAccountId,
    }),
  });

  if (!response.ok) {
    return {
      mainDomainVal: null,
      img9Val: null,
    };
  }
  return response.json();
}

/**
 * @typedef {Object} StateUpdateResult
 * @property {boolean} success
 * @property {string|null} errorMessage
 */

/**
 * Saves local storage lsa/lsb values to a user's serialized fingerprint for Target.
 * @param {{ mainDomainVal: string, img9Val: string, vendorAccountId: string }}
 * @returns {Promise<StateUpdateResult>}
 */
async function putSnailMetrixLocalStorage({
  mainDomainVal,
  img9Val,
  vendorAccountId,
}) {
  let success = false;
  let errorMessage = null;
  const defaultErr =
    'There was an error updating custom device information. Please refresh and try again.';

  try {
    const response = await fetch(
      `${getEndpoint()}/snailmetrix/update-storage`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mainDomainVal,
          img9Val,
          vendorAccountId,
        }),
      }
    );

    if (response.ok) {
      success = true;
    } else {
      const respBody = await response.json();
      errorMessage = respBody.error || defaultErr;
    }
  } catch (err) {
    errorMessage = defaultErr;
  }

  return { success, errorMessage };
}

async function getTargetStoreSelection(vendorAccountId) {
  const response = await fetch(`${getEndpoint()}/target/fetch-stores`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      vendorAccountId,
    }),
  });

  if (!response.ok) {
    return {
      tgStores: [],
    };
  }
  return response.json();
}

async function putTargetStoreSelection({ vendorAccountId, tgStores }) {
  let success = false;
  let errorMessage = null;
  let updatedTgStores = [];
  const defaultErr =
    'There was an error updating your Target store selection. Please refresh and try again.';

  const selectedStoreIds = tgStores
    .filter((store) => !store.disabled)
    .map((store) => store.storeId);

  try {
    const response = await fetch(`${getEndpoint()}/target/update-stores`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        vendorAccountId,
        selectedStoreIds,
      }),
    });

    const respBody = await response.json();
    if (response.ok) {
      success = true;
      updatedTgStores = respBody.tgStores;
    } else {
      errorMessage = respBody.error || defaultErr;
    }
  } catch (err) {
    errorMessage = defaultErr;
  }

  return {
    success,
    errorMessage,
    tgStores: updatedTgStores,
  };
}

function createGoogleMapsSearchUrl(locationName, address) {
  const encodedSearch = encodeURIComponent(`${locationName}, ${address}`);
  return `https://www.google.com/maps/search/${encodedSearch}/`;
}

function createTargetStoreUrl(storeId) {
  // The `store` in the url here is actually a placeholder slug.
  // The url accepts any slug here, but having it missing in the
  // url completely doesn't work.
  return `https://www.target.com/sl/store/${storeId}`;
}

export default {
  getUrlParamVal,
  isProUpgradeExpired,
  saveCredIdToCookie,
  getCredIdFromCookie,
  log,
  getEndpoint,
  storageSet,
  storageGet,
  storageClear,
  titleCase,
  isDevMode,
  getUser,
  getProducts,
  getTrackedProducts,
  putTrackedProducts,
  getTrends,
  getOrders,
  putCredentials,
  getCredentials,
  getEvents,
  EVENT_TYPES,
  AM_SKIP_REASONS,
  getCheckouts,
  putWebhook,
  getUserAgent,
  isPC,
  getCookies,
  setCookie,
  submitTestOrder,
  getAsinFromDPUrl,
  getTcinFromDPUrl,
  getIPData,
  sleep,
  vendorToAttemptEvent,
  vendorToAttemptSkippedEvent,
  getDpUrlForVendor,
  renderCredBadge,
  maybePluralize,
  makeDereferUrl,
  getPrettyTime,
  getPrettyDate,
  getPrettyDateOrTime,
  constructDate,
  relativeTimeFromDates,
  relativeTimeFromElapsed,
  SHOW_JOIN_DISCORD_COOKIE_NAME: 'showJoinDiscord',
  getStripeCustomerInfo,
  injectSnailMetrix,
  getSnailMetrixLocalStorage,
  putSnailMetrixLocalStorage,
  getTargetStoreSelection,
  putTargetStoreSelection,
  createGoogleMapsSearchUrl,
  createTargetStoreUrl,
};
